import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useClassroom } from "../../../context/ClassroomContext";
import {
  getClassroom,
  getStudentDetail,
} from "../../../services/classroomService";
import { useHandleError } from "../../../hooks/useHandleError";
import { formatModuleLabel } from "../../../utils/moduleUtils";

const StudentDetail: React.FC = () => {
  const { classroomId, studentId } = useParams<{
    classroomId: string;
    studentId: string;
  }>();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { classroom, setClassroom } = useClassroom();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!classroomId) return;
    if (!classroom || classroom._id !== classroomId) {
      getClassroom(classroomId)
        .then((res) => res.data && setClassroom(res.data))
        .catch(handleError);
    }
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId || !studentId) return;
    setLoading(true);
    getStudentDetail(classroomId, studentId)
      .then((res) => setData(res.data))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [classroomId, studentId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Student not found.</Typography>
      </Box>
    );
  }

  const { student, placementTest, courseProgress, quizSubmissions } = data;

  const quizColumns: GridColDef[] = [
    {
      field: "moduleId",
      headerName: "Module",
      width: 280,
      valueGetter: (params) => formatModuleLabel(params.value),
    },
    {
      field: "score",
      headerName: "Score",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const score = params.value as number;
        const color =
          score >= 80 ? "success" : score >= 50 ? "warning" : "error";
        return (
          <Chip
            label={`${Math.round(score || 0)}%`}
            color={color}
            size="small"
            sx={{ fontSize: ".7rem" }}
          />
        );
      },
    },
    {
      field: "points",
      headerName: "Points",
      width: 100,
      valueGetter: (params) =>
        `${params.row.earnedPoints || 0}/${params.row.totalPoints || 0}`,
    },
    {
      field: "timeSpent",
      headerName: "Time",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const ms = params.value as number;
        if (!ms) return "";
        const mins = Math.floor(ms / 60000);
        const secs = Math.floor((ms % 60000) / 1000);
        return `${mins}m ${secs}s`;
      },
    },
    {
      field: "completedAt",
      headerName: "Date",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        return new Date(params.value).toLocaleDateString("en-AU");
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/teacher/classrooms")}
        >
          Classrooms
        </Link>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() =>
            navigate(`/teacher/classrooms/${classroomId}/progress`)
          }
        >
          {classroom?.classroomName || "..."}
        </Link>
        <Typography color="text.primary">
          {student.firstName} {student.lastName}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5">
                {student.firstName} {student.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {student.email}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {student.currentSchool}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              {student.programName && (
                <Chip label={student.programName} color="primary" sx={{ mb: 1 }} />
              )}
              <Typography variant="body2" color="textSecondary">
                Enrolled:{" "}
                {student.enrolledAt
                  ? new Date(student.enrolledAt).toLocaleDateString("en-AU")
                  : "N/A"}
              </Typography>
            </Box>
          </Box>
          {courseProgress && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">
                  Overall Progress: {courseProgress.overallProgress || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={courseProgress.overallProgress || 0}
                sx={{ height: 10, borderRadius: 5, mt: 0.5 }}
                color={
                  courseProgress.overallProgress >= 70
                    ? "success"
                    : courseProgress.overallProgress >= 40
                    ? "warning"
                    : "error"
                }
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Placement Test */}
      {placementTest && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Placement Test Result
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Program
                </Typography>
                <Typography variant="body1">
                  {placementTest.programName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Easy Correct
                </Typography>
                <Typography variant="body1">
                  {placementTest.easyCorrect}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Hard Correct
                </Typography>
                <Typography variant="body1">
                  {placementTest.hardCorrect}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Weekly Breakdown */}
      {courseProgress?.weeklyContent && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Weekly Breakdown
          </Typography>
          {courseProgress.weeklyContent.map((week: any) => (
            <Accordion key={week.weekNumber} defaultExpanded={week.status === "IN PROGRESS"}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", minWidth: 80 }}>
                    {week.name}
                  </Typography>
                  <Chip
                    label={week.status}
                    size="small"
                    color={
                      week.status === "COMPLETED"
                        ? "success"
                        : week.status === "IN PROGRESS"
                        ? "primary"
                        : "default"
                    }
                    sx={{ fontSize: ".7rem" }}
                  />
                  <Box sx={{ flex: 1, mx: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={week.progress || 0}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  <Typography variant="body2">{week.progress || 0}%</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(week.modules || []).map((mod: any) => (
                    <Box
                      key={mod.moduleId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography sx={{ minWidth: 120 }}>{mod.name}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {mod.learningContentCompleted ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                        )}
                        <Typography variant="caption">Content</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {mod.quizCompleted ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                        )}
                        <Typography variant="caption">Quiz</Typography>
                      </Box>
                      {mod.quizScore > 0 && (
                        <Chip
                          label={`Score: ${mod.quizScore}`}
                          size="small"
                          sx={{ fontSize: ".65rem" }}
                        />
                      )}
                      {mod.quizAttempts > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          {mod.quizAttempts} attempt{mod.quizAttempts > 1 ? "s" : ""}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Quiz History */}
      <Typography variant="h6" gutterBottom>
        Quiz History
      </Typography>
      <DataGrid
        rows={(quizSubmissions || []).map((s: any, i: number) => ({ ...s, _rowId: i }))}
        columns={quizColumns}
        getRowId={(row) => row._rowId}
        autoHeight
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25]}
      />
    </Box>
  );
};

export default StudentDetail;
